import React from "react";
import { motion } from "framer-motion";
import { Loader2, Lock, MapPin, Phone, Unlock, Heart } from "lucide-react";
import { normTipo, cercoLabel } from "@/data/formOptions";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });

export const formatPrice = (cents) => `€ ${(cents / 100).toFixed(2).replace(".", ",")}`;

export const mutuoLabel = (p) => {
  if (!p) return null;
  if (p.startsWith("Contanti") || p.includes("Contanti")) return "Disponibilità immediata";
  if (p.includes("già fatto")) return "Mutuo pre-approvato";
  if (p.includes("da fare")) return "Mutuo da valutare";
  return "Mutuo";
};

export const PublicRequestCard = ({ r, contatto, onUnlock, unlocking, compact = false }) => {
  const revealed = contatto || (r.sbloccata ? { nome: r.nome, whatsapp: r.whatsapp, email: r.email } : null);
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="bg-white rounded-3xl border border-slate-200/80 shadow-[0_10px_30px_rgba(10,25,47,0.05)] hover:shadow-[0_20px_40px_rgba(10,25,47,0.08)] hover:-translate-y-1 transition-all duration-300 p-6 flex flex-col"
      data-testid={`public-card-${r.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <span className="inline-block rounded-full bg-[#0A192F] text-white text-[11px] font-extrabold tracking-wide px-3 py-1">
            {cercoLabel(r.tipo_ricerca)}
          </span>
          {r.destinazione && (
            <span className="inline-block rounded-full bg-amber-50 border border-amber-300 text-amber-700 text-[11px] font-bold px-2.5 py-1 ml-1.5" data-testid={`public-destinazione-${r.id}`}>
              {r.destinazione}
            </span>
          )}
          {(r.tipi_immobile || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {r.tipi_immobile.map((t) => (
                <span key={t} className="rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold px-2.5 py-1">
                  {t}
                </span>
              ))}
            </div>
          )}
          <p className="text-xs text-slate-400 mt-2">{formatDate(r.created_at)}</p>
        </div>
        <p className="font-display font-extrabold text-xl text-[#0A192F] whitespace-nowrap">
          € {Number(r.budget_max).toLocaleString("it-IT")}
        </p>
      </div>

      <div className="flex items-center gap-1.5 text-sm font-bold text-[#0A192F] mb-3">
        <MapPin className="w-4 h-4 text-[#10B981] shrink-0" />
        {r.comune} ({r.provincia})
        {r.raggio_km ? <span className="text-slate-400 font-normal">+{r.raggio_km} km</span> : null}
      </div>

      {!compact && (
        <>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {[
              r.camere_min && `${r.camere_min}+ camere`,
              r.bagni_min && `${r.bagni_min}+ bagni`,
              r.mq,
              ...(r.zone || []),
              ...(r.caratteristiche || []),
            ]
              .filter(Boolean)
              .map((chip) => (
                <span key={chip} className="rounded-full bg-slate-50 border border-slate-200 text-slate-600 text-[11px] font-medium px-2.5 py-1">
                  {chip}
                </span>
              ))}
          </div>
          {r.periodo && (
            <p className="text-xs font-bold text-[#0A192F] bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 inline-block" data-testid={`public-periodo-${r.id}`}>
              📅 Periodo: {r.periodo}
            </p>
          )}
          {mutuoLabel(r.pagamento_acquisto) && (
            <p className="text-xs font-bold text-[#0A192F] bg-sky-50 border border-sky-200 rounded-lg px-3 py-2 mb-3 inline-block ml-2" data-testid={`public-mutuo-${r.id}`}>
              💶 {mutuoLabel(r.pagamento_acquisto)}
            </p>
          )}
          {r.note && (
            <p className="text-xs text-slate-500 leading-relaxed italic border-l-2 border-emerald-200 pl-3 mb-3">
              “{r.note}”
            </p>
          )}
        </>
      )}

      <div className="mt-auto pt-4 border-t border-slate-100">
        {revealed ? (
          <div data-testid={`public-contatto-${r.id}`} className="space-y-1.5">
            <p className="text-sm font-extrabold text-[#0A192F]">{revealed.nome}</p>
            <a
              href={`https://wa.me/${(revealed.whatsapp || "").replace(/[^0-9]/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 text-sm font-bold text-[#10B981] hover:underline"
            >
              <Phone className="w-3.5 h-3.5" /> {revealed.whatsapp}
            </a>
            {revealed.email && <p className="text-xs text-slate-500">{revealed.email}</p>}
            {r.u && (
              <a
                href={`/chat?u=${r.u}`}
                data-testid={`public-match-${r.id}`}
                className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-[#0A192F] text-white text-xs font-extrabold px-4 py-2 hover:bg-[#10B981] transition-colors"
              >
                <Heart className="w-3.5 h-3.5 fill-[#10B981] text-[#10B981]" /> Apri il Match
              </a>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-slate-400">
              <Lock className="w-4 h-4 shrink-0" />
              <div className="leading-tight">
                <p className="text-xs font-semibold blur-[3px] select-none">Mario R.</p>
                <p className="text-[10px] blur-[3px] select-none">333 ••• ••••</p>
              </div>
            </div>
            <button
              onClick={() => onUnlock(r)}
              disabled={unlocking === r.id}
              data-testid={`public-unlock-${r.id}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#10B981] text-white text-xs font-extrabold px-4 py-2.5 hover:bg-[#059669] hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60"
            >
              {unlocking === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlock className="w-3.5 h-3.5" />}
              Ricevi il contatto · {formatPrice(r.prezzo_sblocco_cents || 399)}
            </button>
          </div>
        )}
      </div>
    </motion.article>
  );
};
